import {
  absBig,
  BIG_ONE,
  BIG_ZERO,
  formatBig,
  gcdBig,
} from "@/lib/tool-kit/math/bigint";
import { simplifySqrt } from "@/lib/tool-kit/math/radical";
import {
  addR,
  divR,
  formatStyled,
  fromBigInt,
  intR,
  isZeroR,
  mulR,
  type NumberStyle,
  negR,
  RATIONAL_ZERO,
  type Rational,
  signR,
  subR,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

/**
 * Numbers a + b√d with a, b rational and d a square-free integer.
 *
 * This is the field Q(√d): it contains every eigenvalue of a 2 × 2 rational matrix, and
 * with d negative the complex ones (√−5 = i√5). Arithmetic stays exact, so eigenvectors
 * of the golden-ratio matrix come out as (1 + √5)/2 rather than 1.6180339887….
 *
 * A number with b = 0 is stored with d = 1, so every rational has one representation.
 */
export interface QuadNumber {
  a: Rational;
  b: Rational;
  /** Square-free, ≠ 0; 1 exactly when b = 0. Negative for an imaginary radical. */
  d: bigint;
}

export const Q_ZERO: QuadNumber = {
  a: RATIONAL_ZERO,
  b: RATIONAL_ZERO,
  d: BIG_ONE,
};
export const Q_ONE: QuadNumber = { a: intR(1), b: RATIONAL_ZERO, d: BIG_ONE };

export function quadFromRational(a: Rational): QuadNumber {
  return { a, b: RATIONAL_ZERO, d: BIG_ONE };
}

function normalize(a: Rational, b: Rational, d: bigint): QuadNumber {
  return isZeroR(b) ? quadFromRational(a) : { a, b, d };
}

/** √r for a rational r (negative allowed): coefficient × √d, d square-free. */
export function sqrtRational(r: Rational): QuadNumber {
  const negative = signR(r) < 0;
  const magnitude = negative ? negR(r) : r;
  // √(n/m) = √(n·m) / m
  const root = simplifySqrt(magnitude.num * magnitude.den);
  const coefficient = divR(
    fromBigInt(root.coefficient),
    fromBigInt(magnitude.den)
  );
  if (root.radicand === BIG_ONE && !negative) {
    return quadFromRational(coefficient);
  }
  return normalize(
    RATIONAL_ZERO,
    coefficient,
    negative ? -root.radicand : root.radicand
  );
}

function commonD(x: QuadNumber, y: QuadNumber): bigint {
  return isZeroR(x.b) ? y.d : x.d;
}

export function addQ(x: QuadNumber, y: QuadNumber): QuadNumber {
  return normalize(addR(x.a, y.a), addR(x.b, y.b), commonD(x, y));
}

export function subQ(x: QuadNumber, y: QuadNumber): QuadNumber {
  return normalize(subR(x.a, y.a), subR(x.b, y.b), commonD(x, y));
}

export function negQ(x: QuadNumber): QuadNumber {
  return normalize(negR(x.a), negR(x.b), x.d);
}

export function mulQ(x: QuadNumber, y: QuadNumber): QuadNumber {
  const d = commonD(x, y);
  const dr = fromBigInt(d);
  return normalize(
    addR(mulR(x.a, y.a), mulR(mulR(x.b, y.b), dr)),
    addR(mulR(x.a, y.b), mulR(x.b, y.a)),
    d
  );
}

export function isZeroQ(x: QuadNumber): boolean {
  return isZeroR(x.a) && isZeroR(x.b);
}

export function isRationalQ(x: QuadNumber): boolean {
  return isZeroR(x.b);
}

export function conjugate(x: QuadNumber): QuadNumber {
  return normalize(x.a, negR(x.b), x.d);
}

export function divQ(x: QuadNumber, y: QuadNumber): QuadNumber {
  const d = commonD(x, y);
  const norm = subR(mulR(y.a, y.a), mulR(mulR(y.b, y.b), fromBigInt(d)));
  const top = mulQ(x, conjugate(y));
  return normalize(
    divR(top.a, norm),
    divR(top.b, norm),
    top.d === BIG_ONE ? d : top.d
  );
}

export function scaleQ(x: QuadNumber, k: Rational): QuadNumber {
  return normalize(mulR(x.a, k), mulR(x.b, k), x.d);
}

/** Real and imaginary parts as floats. */
export function quadToComplex(x: QuadNumber): { re: number; im: number } {
  const a = toNumberR(x.a);
  const b = toNumberR(x.b);
  if (x.d < BIG_ZERO) {
    return { re: a, im: b * Math.sqrt(Number(-x.d)) };
  }
  return { re: a + b * Math.sqrt(Number(x.d)), im: 0 };
}

/* ---------------------------------------------------------------- notation */

function lcmBigInt(a: bigint, b: bigint): bigint {
  return (a / gcdBig(a, b)) * b;
}

/** "√5", "i√5", "i" for the radical unit of d. */
function unitOf(d: bigint): string {
  if (d === -BIG_ONE) {
    return "i";
  }
  return d < BIG_ZERO ? `i√${(-d).toString()}` : `√${d.toString()}`;
}

/**
 * The number as one fraction over a whole-number denominator:
 * "(1 + √5)/2", "−1 + 2i", "(−1 + i√3)/2", "√2/2", "3/2".
 */
export function formatQuad(
  x: QuadNumber,
  style: NumberStyle = "fraction",
  decimalMark = "."
): string {
  if (isZeroR(x.b)) {
    return formatStyled(x.a, style, decimalMark);
  }
  const denominator = lcmBigInt(x.a.den, x.b.den);
  const p = (x.a.num * denominator) / x.a.den;
  const q = (x.b.num * denominator) / x.b.den;
  const unit = unitOf(x.d);
  const magnitude = absBig(q);
  const radical =
    magnitude === BIG_ONE ? unit : `${magnitude.toString()}${unit}`;
  const sign = q < BIG_ZERO ? "−" : "+";
  let numerator = "";
  if (p === BIG_ZERO) {
    numerator = `${q < BIG_ZERO ? "−" : ""}${radical}`;
  } else {
    numerator = `${formatBig(p)} ${sign} ${radical}`;
  }
  if (denominator === BIG_ONE) {
    return numerator;
  }
  return p === BIG_ZERO
    ? `${numerator}/${denominator}`
    : `(${numerator})/${denominator}`;
}
